import { evaluateInScope } from '../component/utils.js'

export default {

    signature: 'qv-on:',

    handle(node, scope) {

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null
        }

        if (node.hasAttribute('qv-for')) {
            return null
        }

        const cleanupCallbacks = []

        for (const attribute of node.attributes) {

            if (!attribute.name.startsWith(this.signature)) {
                continue
            }

            const rawEventName = attribute.name.slice(this.signature.length)
            const eventParts = rawEventName.split('.')
            const eventName = eventParts[ 0 ]
            const modifiers = eventParts.slice(1)
            const expression = attribute.value
            const options = {}
            const useWindowTarget = modifiers.includes('window')
            const hasOutsideModifier = eventName === 'click' && modifiers.includes('outside')
            const filteredModifiers = modifiers.filter(modifier => ![ 'window', 'outside' ].includes(modifier))

            if (modifiers.includes('passive')) {
                options.passive = true
            }

            if (modifiers.includes('once')) {
                options.once = true
            }

            if (modifiers.includes('capture')) {
                options.capture = true
            }

            const handler = event => {

                // Check for key modifiers if this is a keydown/keyup/keypress event
                if ([ 'keydown', 'keyup', 'keypress' ].includes(eventName) && !matchesKeyModifiers(event, filteredModifiers)) {
                    return
                }

                if (filteredModifiers.includes('prevent')) {
                    event.preventDefault()
                }

                if (filteredModifiers.includes('stop')) {
                    event.stopPropagation()
                }

                evaluateInScope(expression, scope, { $event: event })

            }

            if (hasOutsideModifier) {

                const outsideHandler = event => {

                    // Check if the click is outside the element and its children
                    if (node && node.contains(event.target)) {
                        return
                    }

                    handler(event)

                }

                // For outside clicks, we need to listen on the document
                document.addEventListener('click', outsideHandler, options)

                cleanupCallbacks.push(() => {
                    document.removeEventListener('click', outsideHandler, options)
                })

            } else {

                // Attach event listener to window or the element based on the modifier
                const target = useWindowTarget ? window : node

                target.addEventListener(eventName, handler, options)

                // Store cleanup function for later execution
                cleanupCallbacks.push(() => {
                    target.removeEventListener(eventName, handler, options)
                })

            }

        }

        // Return a single cleanup function that calls all individual cleanups
        return () => {

            for (const callback of cleanupCallbacks) {
                callback()
            }

        }

    },
}

function matchesKeyModifiers(event, modifiers) {

    const keyModifiers = modifiers.filter(modifier => ![ 'prevent', 'stop' ].includes(modifier))

    if (keyModifiers.length === 0) {
        return true
    }

    const pressedKey = event.key?.toLowerCase()

    const keyMap = {
        down: 'arrowdown',
        up: 'arrowup',
        left: 'arrowleft',
        right: 'arrowright',
        enter: 'enter',
        esc: 'escape',
        escape: 'escape',
        space: ' ',
        tab: 'tab',
        backspace: 'backspace',
    }

    const matchesKey = keyModifiers.some(modifier => {

        const mappedKey = keyMap[ modifier.toLowerCase() ] || modifier.toLowerCase()

        return mappedKey === pressedKey

    })

    if (matchesKey) {
        return true
    }

    return false

}
